/**
 * Join tickets: one click in the browser, the computer joins by itself (ADR 0090 decision 2,
 * ADR 0042 Flow D for people; R-ON-3, R-ON-1, R-SC-2).
 *
 * A signed-in person (operator or above, never an agent or an API token) mints a ticket in Add
 * machine. It is single use, lives JOIN_TICKET_TTL_MS, is scoped to their workspace and their
 * personal machine scope, and is stored only as a hash. The console copies the link
 * `${origin}${JOIN_PAGE}/<ticket>`; the agent reads it from the clipboard once at first run (or
 * from `--ticket-file` / `--ticket-stdin`) and sends the secret in its pairing request
 * (PairCreateRequest.ticket, beside kHash). The server then claims the request for the minter and
 * approves it exactly as a person would, into their personal scope. A refused ticket leaves the
 * request created and unclaimed, so the node falls back to Flow A (the browser page) with the
 * same request. A second key presenting a used ticket stops both requests (a conflict: the first
 * computer is removed again) and raises an alert.
 *
 *   Console (signed in, people only)
 *     POST   /api/pair/tickets          mint            {} -> JoinTicketCreated (201)
 *     GET    /api/pair/tickets          my open tickets -> JoinTicket[] (open ones, newest first)
 *     DELETE /api/pair/tickets/:id      cancel          -> JoinTicket (state 'cancelled')
 *
 * The ticket never appears in a URL the agent downloads from, in the installer, in a query string
 * or in a log line: the `/join/<ticket>` page reads it from its path only to show it as a QR code,
 * and never redeems anything itself.
 *
 * Encoding: 16 random bytes as unpadded base64url (22 characters). The server stores
 * joinTicketHash: unpadded base64url SHA-256 over the UTF-8 text of the ticket.
 */

/** Mint, list and cancel (console). */
export const PAIR_TICKETS_API = '/api/pair/tickets';
/** The page the link opens in a browser: `${origin}${JOIN_PAGE}/<ticket>`. */
export const JOIN_PAGE = '/join';
/** A ticket not used within this long expires. */
export const JOIN_TICKET_TTL_MS = 15 * 60_000;
/** Open (unused, unexpired, not cancelled) tickets one person may hold at once. */
export const JOIN_TICKETS_OPEN_MAX = 5;
/** What a ticket looks like on the wire: base64url, at least 20 characters (22 when minted), at most 64. */
export const JOIN_TICKET_RE = /^[A-Za-z0-9_-]{20,64}$/;

/**
 * open       minted, not used, not expired
 * used       a computer joined with it (single use)
 * expired    nobody used it within JOIN_TICKET_TTL_MS
 * cancelled  its person cancelled it
 * conflict   a second computer presented it after it was used: both requests were stopped
 */
export type JoinTicketState = 'open' | 'used' | 'expired' | 'cancelled' | 'conflict';

/** GET /api/pair/tickets, one row. Never the secret. */
export interface JoinTicket {
  id: string;
  state: JoinTicketState;
  createdAt: number;
  expiresAt: number;
  /** True once a computer joined with it. */
  used: boolean;
  /** When it was used, and the machine it became. */
  usedAt?: number;
  machineId?: string;
  machineName?: string;
}

/** 201 to POST /api/pair/tickets. `url` holds the secret: shown once, never stored by the server. */
export interface JoinTicketCreated {
  id: string;
  /** `${public origin}${JOIN_PAGE}/<ticket>`. */
  url: string;
  createdAt: number;
  expiresAt: number;
}

/** Why a ticket in a pairing request was not used (the request stays, unclaimed: Flow A). */
export type JoinTicketRefusal = 'unknown' | 'expired' | 'used' | 'cancelled' | 'role' | 'needs-admin';

/**
 * PairCreated.ticket (additive): what the server did with the ticket the request carried.
 * 'redeemed': the request is approved already, poll for the grant now (no browser). 'refused':
 * the request is unclaimed as usual; show `message` and open the approval page (Flow A).
 */
export interface PairTicketOutcome {
  state: 'redeemed' | 'refused';
  /** One sentence the node shows (R-ON-6). */
  message: string;
  reason?: JoinTicketRefusal;
}

/** The sentence the node shows for each refusal: what happened, and that it falls back to the browser. */
export const JOIN_TICKET_REFUSAL_MESSAGE: Record<JoinTicketRefusal, string> = {
  unknown: 'The join link was not recognised, so approve this computer in the browser instead.',
  expired: 'The join link expired after 15 minutes, so approve this computer in the browser instead.',
  used: 'The join link was already used, so approve this computer in the browser instead.',
  cancelled: 'The join link was cancelled, so approve this computer in the browser instead.',
  role: 'The person who made the join link can no longer add computers, so approve this computer in the browser instead.',
  'needs-admin': 'This computer needs an admin to approve it, so approve it in the browser instead.',
};

/** The sentence the node shows when its ticket was redeemed. */
export const JOIN_TICKET_REDEEMED_MESSAGE = 'Joined with the link from your browser. Connecting now.';

/* Their audit labels are in PAIR_AUDIT_LABEL: pairing.ticket-minted, pairing.ticket-cancelled, pairing.ticket-refused. */

/** The ticket at the end of a join link of this origin, or null (the agent's clipboard matcher, in TypeScript). */
export function joinTicketFromUrl(text: string, origin: string): string | null {
  const t = text.trim();
  const base = origin.replace(/\/+$/, '');
  if (!base.startsWith('https://') || !t.startsWith(`${base}${JOIN_PAGE}/`)) return null;
  const ticket = t.slice(base.length + JOIN_PAGE.length + 1);
  return JOIN_TICKET_RE.test(ticket) ? ticket : null;
}
